import {
  Archive,
  ChevronRight,
  Language,
  Logout,
  Menu as MenuIcon,
  PersonOutline,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "@remix-run/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "~/components/AuthProvider";
import crosAirLogo from "~/assets/icons/cros-air-logo.svg";
import { ChangeLanguageModal } from "~/components/ChangeLanguageModal";
import { LogoutConfirmModal } from "~/components/LogoutConfirmModal";
import { DeleteAccountFlow } from "~/components/DeleteAccountFlow";
import { trpc } from "~/utils/trpc";
import { LANGUAGES, type LanguageCode } from "~/utils/languages";

interface AuthTopNavProps {
  loggedIn?: boolean;
}

export function AuthTopNav({ loggedIn = false }: AuthTopNavProps) {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Desktop menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Mobile expanded panel
  const [mobileExpanded, setMobileExpanded] = useState(false);
  // Modals
  const [langDialogOpen, setLangDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [deleteFlowOpen, setDeleteFlowOpen] = useState(false);

  const handleDesktopMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDesktopMenuClose = () => {
    setAnchorEl(null);
  };

  const openLangDialog = () => {
    setLangDialogOpen(true);
    handleDesktopMenuClose();
    setMobileExpanded(false);
  };

  const updateLocale = trpc.user.updateLocale.useMutation();

  const handleSaveLanguage = (lang: LanguageCode) => {
    i18n.changeLanguage(lang);
    if (loggedIn) {
      updateLocale.mutate({ locale: lang });
    }
    setLangDialogOpen(false);
  };

  const handleLogout = () => {
    handleDesktopMenuClose();
    setMobileExpanded(false);
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    navigate("/api/auth/sign-out");
  };

  const archivedTeamsQuery = trpc.team.listArchived.useQuery(undefined, {
    enabled: loggedIn,
  });
  const hasArchivedTeams = (archivedTeamsQuery.data?.length ?? 0) > 0;

  const currentLangLabel =
    LANGUAGES.find((l) => l.code === i18n.language)?.label ?? "English";

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && mobileExpanded && (
        <Box
          onClick={() => setMobileExpanded(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.6)",
            zIndex: 1099,
          }}
        />
      )}

      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid #e9ebee",
          zIndex: 1100,
        }}
      >
        <Toolbar
          sx={{
            height: { xs: 56, sm: 64 },
            minHeight: { xs: 56, sm: 64 },
            justifyContent: "space-between",
            px: { xs: 2.5, sm: 3 },
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={crosAirLogo}
            alt="CROS AIR"
            sx={{ height: 16, width: "auto" }}
          />

          {/* === Desktop right side (sm+) === */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            {loggedIn ? (
              <Box>
                <IconButton onClick={handleDesktopMenuOpen} size="small">
                  <Avatar
                    src={auth.authData?.avatarUrl}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "#e9ebee",
                      border: anchorEl
                        ? "2px solid #1868db"
                        : "1px solid #d0d6db",
                    }}
                  >
                    <PersonOutline sx={{ fontSize: 20, color: "#9ca8b4" }} />
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleDesktopMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: {
                      sx: {
                        minWidth: 140,
                        mt: 1,
                        borderRadius: "8px",
                        border: "1px solid #e9ebee",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
                        py: 1,
                      },
                    },
                  }}
                >
                  {hasArchivedTeams && (
                    <MenuItem
                      onClick={() => {
                        handleDesktopMenuClose();
                        navigate("/archived-teams");
                      }}
                      sx={{ minHeight: 44, px: 2, gap: 1.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: "auto" }}>
                        <Archive sx={{ fontSize: 24, color: "#374553" }} />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{
                          fontSize: 14,
                          color: "#374553",
                        }}
                      >
                        {t("team.archivedTeams")}
                      </ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={openLangDialog}
                    sx={{ minHeight: 44, px: 2, gap: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Language sx={{ fontSize: 24, color: "#374553" }} />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: 14,
                        color: "#374553",
                      }}
                    >
                      {t("auth.language")}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={handleLogout}
                    sx={{ minHeight: 44, px: 2, gap: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Logout sx={{ fontSize: 24, color: "#374553" }} />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{
                        fontSize: 14,
                        color: "#374553",
                      }}
                    >
                      {t("auth.signOut")}
                    </ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            ) : (
              <Box
                onClick={openLangDialog}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                }}
              >
                <Language sx={{ fontSize: 20, color: "#1868db" }} />
                <Typography
                  sx={{ fontSize: 14, fontWeight: 500, color: "#1868db" }}
                >
                  {t("auth.language")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* === Mobile right side (xs) === */}
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            {loggedIn ? (
              <IconButton
                onClick={() => setMobileExpanded((v) => !v)}
                size="small"
              >
                <Avatar
                  src={auth.authData?.avatarUrl}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "#e9ebee",
                    border: mobileExpanded
                      ? "2px solid #1868db"
                      : "1px solid #d0d6db",
                  }}
                >
                  <PersonOutline sx={{ fontSize: 20, color: "#9ca8b4" }} />
                </Avatar>
              </IconButton>
            ) : (
              <IconButton
                onClick={() => setMobileExpanded((v) => !v)}
                size="small"
              >
                <MenuIcon sx={{ fontSize: 24, color: "#374553" }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>

        {/* === Mobile expanded panel === */}
        {isMobile && mobileExpanded && (
          <Box
            sx={{
              bgcolor: "white",
              pb: 1,
            }}
          >
            {/* Archived Teams row */}
            {hasArchivedTeams && (
              <Box
                onClick={() => {
                  setMobileExpanded(false);
                  navigate("/archived-teams");
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minHeight: 52,
                  px: 2.5,
                  py: 1.5,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Archive sx={{ fontSize: 24, color: "#374553" }} />
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 500, color: "#374553" }}
                  >
                    {t("team.archivedTeams")}
                  </Typography>
                </Box>
                <ChevronRight sx={{ fontSize: 24, color: "#374553" }} />
              </Box>
            )}

            {/* Language row */}
            <Box
              onClick={openLangDialog}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minHeight: 52,
                px: 2.5,
                py: 1.5,
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Language sx={{ fontSize: 24, color: "#374553" }} />
                <Typography
                  sx={{ fontSize: 16, fontWeight: 500, color: "#374553" }}
                >
                  {currentLangLabel}
                </Typography>
              </Box>
              <ChevronRight sx={{ fontSize: 24, color: "#374553" }} />
            </Box>

            {/* Log out row (logged in only) */}
            {loggedIn && (
              <Box
                onClick={handleLogout}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minHeight: 52,
                  px: 2.5,
                  py: 1.5,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Logout sx={{ fontSize: 24, color: "#374553" }} />
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 500, color: "#374553" }}
                  >
                    {t("auth.logout.title")}
                  </Typography>
                </Box>
                <ChevronRight sx={{ fontSize: 24, color: "#374553" }} />
              </Box>
            )}
          </Box>
        )}
      </AppBar>

      <ChangeLanguageModal
        open={langDialogOpen}
        onClose={() => setLangDialogOpen(false)}
        currentLanguage={i18n.language.startsWith("ko") ? "ko" : "en"}
        onSave={handleSaveLanguage}
      />

      <LogoutConfirmModal
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
        onDeleteAccount={() => {
          setLogoutDialogOpen(false);
          setDeleteFlowOpen(true);
        }}
      />

      <DeleteAccountFlow
        open={deleteFlowOpen}
        email={auth.authData?.email ?? ""}
        onClose={() => setDeleteFlowOpen(false)}
      />
    </>
  );
}
